import React from "react";
import LegalLayout from "../components/legal/LegalLayout";
import { SEO } from "../components/seo/SEO";
import { getPageSeo } from "../lib/seoConfig";

export default function Privacy() {
  const seo = getPageSeo("/privacy");
  return (
    <LegalLayout title="Privacy Policy" updated="August 25, 2026">
      <SEO title={seo.title} description={seo.description} path="/privacy" />
      <section>
        <p>
          ServiceGuru Pro ("<strong>ServiceGuru Pro</strong>", "we", "us", or "our") is a business registered in the
          State of Florida (Registration No. G26000098018). This Privacy Policy explains how we collect, use, store,
          and protect information when you use our website and our software platform (the "Service"). By using the
          Service, you agree to the practices described in this policy.
        </p>
      </section>

      <section>
        <h2>1. Information We Collect</h2>
        <p>We collect the following categories of personal and business information:</p>
        <ul>
          <li><strong>Contact information:</strong> name, email address, and phone number of account holders, staff, and their customers.</li>
          <li><strong>Business information:</strong> company name, address, trade/industry, staff roles, job and scheduling records, customer records, estimates, and invoices you create in the Service.</li>
          <li><strong>Payment data:</strong> billing address and payment method details, processed and stored by our payment processor (Stripe) — ServiceGuru Pro does not store full card numbers.</li>
          <li><strong>Communications data:</strong> the content and metadata of SMS messages, voice calls, and emails sent through the Service (for example, appointment confirmations or AI follow-up calls).</li>
          <li><strong>Usage data:</strong> log data, device/browser information, and how you interact with the Service, collected automatically.</li>
        </ul>
      </section>

      <section>
        <h2>2. How We Use Your Information</h2>
        <p>We use the information we collect to:</p>
        <ul>
          <li>Provide, operate, and maintain the Service, including scheduling, dispatch, estimates, invoicing, and customer records;</li>
          <li>Send job updates, appointment confirmations, estimates, invoices, payment reminders, and AI voice follow-up calls on your behalf;</li>
          <li>Process subscription billing and AI credit purchases;</li>
          <li>Provide customer support and respond to inquiries;</li>
          <li>Improve, secure, and troubleshoot the Service; and</li>
          <li>Comply with legal obligations and enforce our Terms of Service.</li>
        </ul>
      </section>

      <section>
        <h2>3. How We Store and Protect Your Data</h2>
        <p>
          Data is stored on secure, access-controlled cloud infrastructure. We use encryption in transit (TLS) for
          data sent to and from the Service, and encrypt sensitive credentials (such as email and payment provider
          secrets) at rest. Access to production data is restricted to authorized personnel who need it to operate
          or support the Service. While we take reasonable technical and organizational measures to protect your
          data, no method of transmission or storage is 100% secure, and we cannot guarantee absolute security.
        </p>
      </section>

      <section>
        <h2>4. Third-Party Services We Use</h2>
        <p>To deliver the Service, we share limited data with the following third-party providers, each bound by their own privacy and security practices:</p>
        <ul>
          <li><strong>Twilio</strong> — sends and receives SMS text messages and phone calls on your behalf (e.g., appointment reminders, job updates).</li>
          <li><strong>ElevenLabs</strong> — powers our AI voice agent for automated calls, such as follow-up calls on open estimates or invoices.</li>
          <li><strong>Stripe</strong> — processes subscription payments and invoice/estimate payments; handles and stores payment card data on our behalf.</li>
          <li><strong>Google OAuth</strong> — used if you choose to connect a Gmail account or sign in with Google, to send email or authenticate your account.</li>
        </ul>
        <p>We do not sell your personal information to third parties.</p>
      </section>

      <section>
        <h2>5. Data Retention</h2>
        <p>
          We retain personal and business data for as long as your account is active and as needed to provide the
          Service. After account cancellation, we retain data for a limited period (typically up to 90 days) to
          allow for account recovery and to comply with legal, accounting, or tax obligations, after which it is
          deleted or anonymized, except where longer retention is required by law.
        </p>
      </section>

      <section>
        <h2>6. Your Rights</h2>
        <p>Depending on your location, you have the right to:</p>
        <ul>
          <li><strong>Access</strong> the personal data we hold about you;</li>
          <li><strong>Correct</strong> inaccurate or incomplete data; and</li>
          <li><strong>Delete</strong> your personal data, subject to legal or contractual retention requirements.</li>
        </ul>
        <p>To exercise any of these rights, contact us at <a href="mailto:info@servicegurupro.com">info@servicegurupro.com</a>.</p>
      </section>

      <section>
        <h2>7. Cookies</h2>
        <p>
          We use cookies and similar technologies to keep you signed in, remember your preferences, and understand
          how the Service is used. You can control cookies through your browser settings; disabling cookies may
          affect how the Service functions.
        </p>
      </section>

      <section>
        <h2>8. SMS &amp; Voice Communications (A2P 10DLC Compliance)</h2>
        <p>
          If you or your customers provide a phone number through the Service, you are opting in to receive text
          messages and/or automated voice calls related to your business's field service operations, including:
        </p>
        <ul>
          <li>Job updates and status notifications;</li>
          <li>Appointment confirmations and reminders;</li>
          <li>Estimates and invoices; and</li>
          <li>AI-powered follow-up calls regarding open estimates, invoices, or scheduling.</li>
        </ul>
        <p>
          <strong>Message frequency</strong> varies based on your account's activity and scheduled jobs — you may
          receive multiple messages per week. <strong>Message and data rates may apply.</strong> To stop receiving
          SMS messages at any time, reply <strong>STOP</strong> to any message. Consent to receive messages is not a
          condition of purchasing any goods or services, and consent is never shared with unaffiliated third
          parties for marketing purposes.
        </p>
      </section>

      <section>
        <h2>9. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We will post the updated version on this page with a
          revised "Last updated" date. Continued use of the Service after changes take effect constitutes
          acceptance of the revised policy.
        </p>
      </section>

      <section>
        <h2>10. Contact Us</h2>
        <p>
          For privacy questions, access/correction/deletion requests, or any other concerns about this policy,
          contact us at <a href="mailto:info@servicegurupro.com">info@servicegurupro.com</a>.
        </p>
        <p className="text-slate-500 text-[13px]">
          ServiceGuru Pro is a business entity registered in the State of Florida, Registration No. G26000098018.
        </p>
      </section>
    </LegalLayout>
  );
}
